import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Scan } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Landing = ({ onAuth, onAdminAuth }) => {
  const [mode, setMode] = useState('faceid');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isScanning, setIsScanning] = useState(false);

  const hasToken = localStorage.getItem('trackcc_token');
  const biometricEnabled = localStorage.getItem('trackcc_biometric') === 'true';

  const handleFaceIDAuth = async () => {
    if (!hasToken) {
      setMode('login');
      return;
    }

    setIsScanning(true);
    
    if (window.PublicKeyCredential) {
      try {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        
        if (available && biometricEnabled) {
          setTimeout(() => {
            setIsScanning(false);
            toast.success('Face ID authenticated');
            onAuth();
          }, 1500);
        } else {
          setTimeout(() => {
            setIsScanning(false);
            onAuth();
          }, 1500);
        }
      } catch (error) {
        setIsScanning(false);
        setMode('login');
      }
    } else {
      setTimeout(() => {
        setIsScanning(false);
        onAuth();
      }, 1500);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('trackcc_token', response.data.access_token);
      localStorage.setItem('trackcc_biometric', 'true');
      toast.success('Login successful');
      onAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/signup`, { email, password });
      localStorage.setItem('trackcc_token', response.data.access_token);
      localStorage.setItem('trackcc_biometric', 'true');
      toast.success('Account created successfully');
      onAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/login`, { password: adminPassword });
      sessionStorage.setItem('admin_password', adminPassword);
      toast.success('Admin login successful');
      onAdminAuth();
    } catch (error) {
      toast.error('Invalid admin password');
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'faceid' || (mode === 'faceid' && hasToken)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md text-center space-y-8"
        >
          <div className="space-y-4">
            <h1 
              className="text-6xl font-extrabold tracking-tight text-foreground"
              data-testid="app-title"
            >
              TRACK CC
            </h1>
            <p className="text-sm text-muted-foreground uppercase tracking-wider">
              Credit Card Spend Tracker
            </p>
          </div>

          <motion.div
            animate={isScanning ? { scale: [1, 1.1, 1], opacity: [1, 0.5, 1] } : {}}
            transition={{ duration: 1, repeat: isScanning ? Infinity : 0 }}
            className="flex justify-center"
          >
            <div 
              className="w-32 h-32 rounded-full border-4 border-primary flex items-center justify-center"
              data-testid="face-id-indicator"
            >
              <Scan size={64} className="text-primary" weight="regular" />
            </div>
          </motion.div>

          <div className="space-y-3">
            <Button
              onClick={handleFaceIDAuth}
              disabled={isScanning}
              size="lg"
              className="w-full h-14 rounded-full text-base font-semibold"
              data-testid="scan-face-button"
            >
              {isScanning ? 'Scanning...' : hasToken ? 'Scan Face to Login' : 'Get Started'}
            </Button>

            {!hasToken && (
              <Button
                onClick={() => setMode('login')}
                variant="outline"
                size="lg"
                className="w-full h-14 rounded-full text-base font-semibold"
              >
                Login with Email
              </Button>
            )}
            
            <button
              onClick={() => setMode('admin')}
              className="text-xs text-muted-foreground underline"
            >
              Admin Login
            </button>
          </div>

          <div className="space-y-1 pb-24">
            <p className="text-xs text-muted-foreground">
              Secure credit card tracker with biometric authentication
            </p>
            <p className="text-xs text-[#800000] font-medium px-4">
              DEMO ONLY: This app is a working demo and does not currently comply with applicable data protection regulations. Also, this tool uses ChatGPT to extract transactions — do not sign up if you are uncomfortable sharing data with ChatGPT.
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  // Admin login form
  if (mode === 'admin') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-center text-2xl font-bold">Admin Login</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAdminLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label>User ID</Label>
                  <Input
                    type="text"
                    value="admin"
                    disabled
                    className="bg-muted text-muted-foreground"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="Enter admin password"
                    required
                  />
                </div>

                <Button type="submit" disabled={loading} className="w-full rounded-full">
                  {loading ? 'Please wait...' : 'Login'}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setMode('faceid')}
                    className="text-sm text-muted-foreground"
                  >
                    Back
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-center text-2xl font-bold">
              {mode === 'login' ? 'Login' : 'Create Account'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={mode === 'login' ? handleLogin : handleSignup} className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-full"
                data-testid="submit-button"
              >
                {loading ? 'Please wait...' : mode === 'login' ? 'Login' : 'Sign Up'}
              </Button>

              <div className="text-center text-sm">
                {mode === 'login' ? (
                  <p>
                    Don't have an account?{' '}
                    <button
                      type="button"
                      onClick={() => setMode('signup')}
                      className="text-primary font-semibold"
                    >
                      Sign up
                    </button>
                  </p>
                ) : (
                  <p>
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={() => setMode('login')}
                      className="text-primary font-semibold"
                    >
                      Login
                    </button>
                  </p>
                )}
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setMode('faceid')}
                  className="text-sm text-muted-foreground"
                >
                  Back to Face ID
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Landing;
